import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { usePublicReceipt } from '@/entities/receipts';
import { DownloadIcon } from '@/shared/ui/icons';
import { formatUsd } from '@/shared/utils/currency';
import { formatDate } from '@/shared/utils/date';

const NAVY = '#0B1C52';
const GOLD = '#EDBB26';

// Small labelled detail row inside the receipt body.
const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5">
    <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-slate-400">
      {label}
    </span>
    <span className="min-w-0 break-words text-right text-sm font-semibold text-slate-800">
      {value || '—'}
    </span>
  </div>
);

// Perforated ticket divider: a dashed line with a notch cut into each side edge.
// The notch circles are painted in the page background colour so they read as
// holes punched out of the card.
const Perforation = () => (
  <div className="relative my-1">
    <span
      aria-hidden
      className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full print:hidden"
      style={{ backgroundColor: '#eef1f7' }}
    />
    <span
      aria-hidden
      className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full print:hidden"
      style={{ backgroundColor: '#eef1f7' }}
    />
    <div className="border-t border-dashed border-slate-300" />
  </div>
);

const CardShell = ({ children }: { children: React.ReactNode }) => (
  <div className="mx-auto w-full max-w-md">
    <div className="overflow-hidden rounded-3xl bg-white shadow-[0_18px_50px_-20px_rgba(11,28,82,0.45)] ring-1 ring-slate-900/5 print:shadow-none print:ring-0">
      {children}
    </div>
  </div>
);

const PageFrame = ({ children }: { children: React.ReactNode }) => (
  <div
    className="min-h-screen w-full px-4 py-8 sm:px-6 sm:py-12 print:bg-white print:py-0"
    style={{ backgroundColor: '#eef1f7' }}
  >
    {children}
  </div>
);

export const PagesReceiptMain = () => {
  const { t } = useTranslation('receipt');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = usePublicReceipt(id);

  // Go back if there is history, otherwise fall back to the home page.
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  // Loading skeleton.
  if (query.isLoading) {
    return (
      <PageFrame>
        <CardShell>
          <div className="h-28 animate-pulse" style={{ backgroundColor: NAVY }} />
          <div className="space-y-4 p-6">
            <div className="mx-auto h-9 w-32 animate-pulse rounded-full bg-slate-100" />
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-4 w-full animate-pulse rounded bg-slate-100" />
              ))}
            </div>
            <div className="h-12 w-full animate-pulse rounded-xl bg-slate-100" />
          </div>
        </CardShell>
      </PageFrame>
    );
  }

  // Not found / error.
  if (query.isError || !query.data) {
    return (
      <PageFrame>
        <CardShell>
          <div className="flex flex-col items-center gap-4 px-6 py-14 text-center">
            <img src="/logo.jpg" alt="Tez Cargo" className="h-14 w-14 rounded-2xl object-cover" />
            <div>
              <h1 className="text-lg font-bold text-slate-900">{t('notFoundTitle')}</h1>
              <p className="mt-1 text-sm text-slate-500">{t('notFoundDescription')}</p>
            </div>
          </div>
        </CardShell>
        <p className="mt-6 text-center text-xs text-slate-400">{t('footer')}</p>
      </PageFrame>
    );
  }

  const r = query.data;
  const amount = formatUsd(Number(r.amount));

  return (
    <PageFrame>
      <CardShell>
        {/* Brand header */}
        <div className="relative px-6 pb-8 pt-7 text-center" style={{ backgroundColor: NAVY }}>
          <div className="flex items-center justify-center gap-3">
            <img
              src="/logo.jpg"
              alt=""
              className="h-11 w-11 rounded-xl object-cover ring-2 ring-white/20"
            />
            <span className="text-xl font-extrabold tracking-tight text-white">
              Tez Cargo
            </span>
          </div>
          <p className="mt-2 text-xs font-medium" style={{ color: GOLD }}>
            {t('brandTagline')}
          </p>
          {/* Gold accent underline */}
          <div
            className="absolute inset-x-0 bottom-0 h-1"
            style={{ backgroundColor: GOLD }}
          />
        </div>

        {/* PAID badge, overlapping the header seam */}
        <div className="relative -mt-4 flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-1.5 text-sm font-bold text-white shadow-md ring-4 ring-white">
            <svg viewBox="0 0 20 20" className="h-4 w-4" fill="currentColor" aria-hidden>
              <path
                fillRule="evenodd"
                d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.3 3.29 6.8-6.8a1 1 0 0 1 1.4 0Z"
                clipRule="evenodd"
              />
            </svg>
            {t('paid')}
          </span>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 pt-5">
          {/* Receipt meta */}
          <div className="flex items-center justify-between gap-4 pb-3">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                {t('receiptNumber')}
              </p>
              <p className="text-base font-bold" style={{ color: NAVY }}>
                {r.number}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                {t('date')}
              </p>
              <p className="text-sm font-semibold text-slate-800">{formatDate(r.created_at)}</p>
            </div>
          </div>

          <Perforation />

          {/* Details */}
          <div className="py-2">
            <DetailRow label={t('customer')} value={r.customer_name} />
            <DetailRow label={t('cargoId')} value={r.cargo_id_code} />
            <DetailRow label={t('parcel')} value={r.parcel_name} />
            <DetailRow label={t('trekId')} value={r.trek_id} />
          </div>

          <Perforation />

          {/* Single payment: one service and its amount as the prominent figure */}
          <div className="py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              {t('service')}
            </p>
            <div className="mt-1.5 flex items-end justify-between gap-4">
              <div className="min-w-0">
                <p className="text-base font-bold text-slate-800">{r.service || '—'}</p>
                {r.description && (
                  <p className="mt-0.5 break-words text-xs text-slate-500">{r.description}</p>
                )}
              </div>
              <span className="shrink-0 text-3xl font-extrabold tabular-nums" style={{ color: NAVY }}>
                {amount}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-2 space-y-2">
            {r.file_url && (
              <a
                href={r.file_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white shadow-sm transition-opacity active:opacity-80 print:hidden"
                style={{ backgroundColor: NAVY }}
              >
                <DownloadIcon className="h-4 w-4" aria-hidden />
                {t('downloadPdf')}
              </a>
            )}
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-bold text-slate-600 transition-colors active:bg-slate-50 print:hidden"
            >
              {t('back')}
            </button>
          </div>
        </div>
      </CardShell>

      {/* Footer */}
      <p className="mx-auto mt-6 max-w-md text-center text-xs text-slate-400">{t('footer')}</p>
    </PageFrame>
  );
};
